<script lang="ts">
	import type { Issue, Report, StatFinding } from '#lib/types';
	import { t } from '#lib/i18n/state.svelte';
	import { CONFIDENCE_UI, confidenceShort, formatPValue, formatNumber } from '#lib/ui/labels';
	import { recomputeP } from '#lib/stats/pvalue';

	interface Props {
		report: Report;
		onjump: (issue: Issue) => void;
	}

	let { report, onjump }: Props = $props();

	const findings = $derived(report.stats.findings);
	let openId = $state<string | null>(null);

	const KIND_SYMBOL: Record<string, string> = {
		t: 't',
		F: 'F',
		chi2: 'χ²',
		r: 'r',
		z: 'z',
		p: 'p'
	};

	function labelFor(f: StatFinding): string {
		const sym = KIND_SYMBOL[f.stat.kind] ?? f.stat.kind;
		const df =
			f.stat.df1 !== undefined
				? `(${f.stat.df1}${f.stat.df2 !== undefined ? `, ${f.stat.df2}` : ''})`
				: '';
		return `${sym}${df} = ${formatNumber(f.stat.value, 2)}`;
	}

	function reportedP(f: StatFinding): string {
		if (f.stat.p === undefined) return '—';
		const op = f.stat.pOp === 'lt' ? '< ' : f.stat.pOp === 'gt' ? '> ' : '= ';
		return `${op}${formatPValue(f.stat.p)}`;
	}

	const uniqueClaims = $derived(() => {
		const seen = new Set<string>();
		return report.stats.claims.filter((c) => {
			const key = `${c.text.toLowerCase()}|${c.hasP}`;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	});

	function formulaFor(f: StatFinding): string {
		const rec = recomputeP({
			kind: f.stat.kind as 't' | 'F' | 'chi2' | 'r' | 'z',
			value: f.stat.value,
			df1: f.stat.df1,
			df2: f.stat.df2,
			n: f.stat.n,
			tails: f.stat.tails
		});
		if (!rec.ok) return t('stats.notComputable');

		const p = formatPValue(rec.result.p);
		const v = formatNumber(f.stat.value, 2);
		const tail = rec.result.tails === 2 ? t('stats.twoTailed') : t('stats.oneTailed');
		switch (f.stat.kind) {
			case 't':
				return `t(${f.stat.df1}) = ${v} · ${tail} → ${t('stats.pShort', { p })}`;
			case 'F':
				return `F(${f.stat.df1}, ${f.stat.df2}) = ${v} → ${t('stats.pShort', { p })}`;
			case 'chi2':
				return `χ²(${f.stat.df1}) = ${v} → ${t('stats.pShort', { p })}`;
			case 'r':
				return `r = ${v}${f.stat.n ? ` (n = ${f.stat.n})` : ''} → ${t('stats.pShort', { p })}`;
			default:
				return `z = ${v} · ${tail} → ${t('stats.pShort', { p })}`;
		}
	}

	function statusOf(f: StatFinding): { label: string; chip: string; tone: string } {
		if (f.stat.confidence === 'low')
			return { label: t('stats.status.unverifiable'), chip: 'chip-neutral', tone: 'var(--muted)' };
		if (f.recomputedP === undefined)
			return { label: t('stats.status.uncomputable'), chip: 'chip-neutral', tone: 'var(--muted)' };
		if (f.thresholdFlip)
			return { label: t('stats.status.flip'), chip: 'chip-critical', tone: '#b3261e' };
		if (f.consistent) return { label: t('stats.status.consistent'), chip: 'chip-ok', tone: '#3f6212' };
		return { label: t('stats.status.deviation'), chip: 'chip-warning', tone: '#9a5b06' };
	}

	const issueByStat = $derived(() => {
		const map = new Map<string, Issue>();
		for (const i of report.issues) if (i.statId) map.set(i.statId, i);
		return map;
	});
</script>

<div class="stats-tab">
	<div class="tiles">
		<div class="tile">
			<span class="n">{report.stats.summary.total}</span>
			<span class="l">{t('stats.total')}</span>
		</div>
		<div class="tile">
			<span class="n" style:color="#3f6212">{report.stats.summary.consistent}</span>
			<span class="l">{t('stats.consistent')}</span>
		</div>
		<div class="tile">
			<span class="n" style:color="#b3261e">{report.stats.summary.inconsistent}</span>
			<span class="l">{t('stats.inconsistent')}</span>
		</div>
		<div class="tile">
			<span class="n" style:color="var(--muted)">{report.stats.summary.unverifiable}</span>
			<span class="l">{t('stats.unverifiable')}</span>
		</div>
	</div>

	{#if findings.length === 0}
		<div class="card empty">
			<p>{t('stats.empty')}</p>
		</div>
	{:else}
		<div class="card table-wrap">
			<table>
				<thead>
					<tr>
						<th scope="col">{t('stats.col.expression')}</th>
						<th scope="col">{t('stats.col.value')}</th>
						<th scope="col">{t('stats.col.df')}</th>
						<th scope="col">{t('stats.col.reported')}</th>
						<th scope="col">{t('stats.col.computed')}</th>
						<th scope="col">{t('stats.col.status')}</th>
						<th scope="col"><span class="sr-only">{t('stats.col.confidence')}</span></th>
					</tr>
				</thead>
				<tbody>
					{#each findings as f (f.stat.id)}
						{@const status = statusOf(f)}
						{@const open = openId === f.stat.id}
						{@const issue = issueByStat().get(f.stat.id)}
						<tr class:row-open={open}>
							<td>
								<button class="raw mono" onclick={() => (openId = open ? null : f.stat.id)} aria-expanded={open}>
									{labelFor(f)}
								</button>
							</td>
							<td class="mono">{formatNumber(f.stat.value, 2)}</td>
							<td class="mono">{f.stat.df1 ?? '—'}{f.stat.df2 !== undefined ? `, ${f.stat.df2}` : ''}
								{f.stat.n !== undefined ? ` · n=${f.stat.n}` : ''}</td>
							<td class="mono">{reportedP(f)}</td>
							<td class="mono">{f.recomputedP !== undefined ? formatPValue(f.recomputedP) : '—'}</td>
							<td><span class="chip {status.chip}">{status.label}</span></td>
							<td>
								<span class="conf" style:color={CONFIDENCE_UI[f.stat.confidence].tone}>
									{confidenceShort(f.stat.confidence)}
								</span>
							</td>
						</tr>
						{#if open}
							<tr class="detail-row">
								<td colspan="7">
									<div class="detail">
										<p class="formula mono">{formulaFor(f)}</p>
										{#if f.stat.p !== undefined && f.recomputedP !== undefined}
											<p class="delta">
												{t('stats.delta')} <span class="mono"
													>{formatPValue(f.recomputedP)} − {formatPValue(f.stat.p)}</span
												>
												{#if f.stat.pBound}
													<span class="chip chip-neutral">{t('stats.boundNote')}</span>
												{/if}
											</p>
										{/if}
										<blockquote>{f.stat.context}</blockquote>
										{#if f.stat.confidenceReasonCodes.length}
											<ul class="reasons">
												{#each f.stat.confidenceReasonCodes as r (r)}<li>{t(`reason.${r}`)}</li>{/each}
											</ul>
										{/if}
										{#if issue && issue.decisive}
											<button class="btn btn-sm" onclick={() => onjump(issue)}>{t('stats.showInText')}</button>
										{/if}
									</div>
								</td>
							</tr>
						{/if}
					{/each}
				</tbody>
			</table>
		</div>
	{/if}

	{#if report.stats.claims.length > 0}
		<section class="claims">
			<h3>{t('stats.claimsTitle')}</h3>
			<p class="small muted">{t('stats.claimsHint')}</p>
			<ul class="claim-list">
				{#each uniqueClaims() as c (c.id)}
					<li>
						<span class="chip {c.hasP ? 'chip-ok' : 'chip-warning'}">
							{c.hasP ? t('stats.supported') : t('stats.unsupported')}
						</span>
						<span class="claim-text">"{c.text}"</span>
						{#if c.negated}<span class="chip chip-neutral">{t('stats.negated')}</span>{/if}
					</li>
				{/each}
			</ul>
		</section>
	{/if}

	<section class="method card">
		<h3>{t('stats.methodTitle')}</h3>
		<ul class="small">
			<li>{t('stats.methodT')}</li>
			<li>{t('stats.methodF')}</li>
			<li>{t('stats.methodChi')}</li>
			<li>{t('stats.methodR')}</li>
			<li>{t('stats.methodZ')}</li>
		</ul>
		<p class="small muted">{t('stats.tolerance')}</p>
	</section>
</div>

<style>
	.stats-tab {
		display: grid;
		gap: 1rem;
	}
	.tiles {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 0.7rem;
	}
	@media (max-width: 720px) {
		.tiles {
			grid-template-columns: repeat(2, 1fr);
		}
	}
	.tile {
		background: var(--card);
		border: 1px solid var(--line);
		border-radius: 11px;
		padding: 0.8rem 0.9rem;
		display: flex;
		flex-direction: column;
	}
	.tile .n {
		font-family: var(--font-display);
		font-size: 1.7rem;
		line-height: 1.1;
		font-variant-numeric: tabular-nums;
	}
	.tile .l {
		font-size: 0.73rem;
		color: var(--muted);
	}
	.table-wrap {
		overflow-x: auto;
	}
	table {
		width: 100%;
		border-collapse: collapse;
		font-size: 0.84rem;
	}
	th {
		text-align: left;
		font-size: 0.7rem;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--muted);
		font-weight: 650;
		padding: 0.65rem 0.8rem;
		border-bottom: 1px solid var(--line);
		background: var(--paper);
		white-space: nowrap;
	}
	td {
		padding: 0.55rem 0.8rem;
		border-bottom: 1px solid var(--line);
		vertical-align: middle;
	}
	tbody tr:last-child td {
		border-bottom: 0;
	}
	tr.row-open td {
		background: var(--paper);
	}
	.raw {
		background: none;
		border: 0;
		padding: 0;
		cursor: pointer;
		text-align: left;
		color: var(--ink);
		border-bottom: 1px dashed var(--line-strong);
	}
	.raw:hover {
		color: var(--accent-ink);
	}
	.conf {
		font-size: 0.72rem;
		font-weight: 600;
	}
	.detail-row td {
		background: var(--paper);
		padding: 0.9rem 1rem 1rem;
	}
	.detail {
		display: grid;
		gap: 0.55rem;
		justify-items: start;
	}
	.formula {
		margin: 0;
		background: #fff;
		border: 1px solid var(--line);
		border-radius: 7px;
		padding: 0.45rem 0.65rem;
		font-size: 0.8rem;
	}
	.delta {
		margin: 0;
		font-size: 0.8rem;
		display: flex;
		gap: 0.5rem;
		align-items: center;
		flex-wrap: wrap;
	}
	blockquote {
		margin: 0;
		padding: 0.5rem 0.7rem;
		background: #fff;
		border-left: 2px solid var(--line-strong);
		font-size: 0.8rem;
		color: var(--ink-2);
		font-style: italic;
	}
	.reasons {
		margin: 0;
		padding-left: 1.1rem;
		font-size: 0.78rem;
		color: var(--muted);
	}
	.empty {
		padding: 1.4rem;
	}
	.empty p {
		margin: 0;
		color: var(--ink-2);
	}
	.claims h3,
	.method h3 {
		font-family: var(--font-ui);
		font-size: 0.92rem;
		font-weight: 650;
		margin: 0 0 0.2rem;
	}
	.claims {
		padding: 0.2rem 0;
	}
	.claim-list {
		list-style: none;
		padding: 0;
		margin: 0.7rem 0 0;
		display: grid;
		gap: 0.4rem;
	}
	.claim-list li {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		font-size: 0.84rem;
		flex-wrap: wrap;
	}
	.claim-text {
		color: var(--ink-2);
	}
	.method {
		padding: 1.1rem 1.2rem;
	}
	.method ul {
		margin: 0.6rem 0;
		padding-left: 1.1rem;
		color: var(--ink-2);
		display: grid;
		gap: 0.25rem;
	}
	.method p {
		margin: 0;
	}

	@media (max-width: 480px) {
		.stats-tab {
			gap: 0.75rem;
		}
		.tiles {
			gap: 0.5rem;
		}
		.tile {
			padding: 0.65rem 0.7rem;
		}
		.tile .n {
			font-size: 1.4rem;
		}
		.method {
			padding: 0.9rem 1rem;
		}
		th,
		td {
			padding: 0.5rem 0.55rem;
		}
	}
</style>
